import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown2, ArrowUp2 } from 'iconsax-react';
import { Image } from 'antd';
import InfoText from '@/components/atoms/InfoText';
import { ProductFeedbackItem } from '@/types/logisticsFeedback';
import { FeedbackProductCustomization } from './FeedbackProductCustomization';

export type ProductRowPart =
  | 'header'
  | 'image'
  | 'name'
  | 'attributes'
  | 'qtyCol'
  | 'qtyLabel'
  | 'qtyValue'
  | 'priceCol'
  | 'priceLabel'
  | 'priceWrapper'
  | 'priceValue'
  | 'currency'
  | 'toggle'
  | 'customization';

interface FeedbackProductRowProps {
  product: ProductFeedbackItem;
  /** Maps each row part to its (locked) data-testid so existing QA ids are preserved. */
  getTestId: (part: ProductRowPart) => string;
}

export const FeedbackProductRow: React.FC<FeedbackProductRowProps> = ({
  product,
  getTestId,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  const hasCustomization = Boolean(product.customization);

  const variationAttributes = (product.variations || [])
    .map((v) => `${v.key}: ${v.value}`)
    .filter(Boolean);

  const fallbackAttributes = [
    product.color ? `${t('logisticsFeedback.color')}: ${product.color}` : null,
    product.size ? `${t('logisticsFeedback.size')}: ${product.size}` : null,
  ].filter(Boolean);

  const attributesText = (
    variationAttributes.length > 0 ? variationAttributes : fallbackAttributes
  ).join(' - ');

  return (
    <div className="space-y-3">
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 select-none"
        data-testid={getTestId('header')}
      >
        <div className="flex flex-col gap-y-2 sm:flex-row sm:items-center sm:gap-x-3 min-w-0">
          <Image
            src={product.imageUrl}
            alt={product.name}
            width={48}
            height={48}
            className="rounded-xl border border-light-input-border dark:border-dark-input-border object-cover shrink-0"
            data-testid={getTestId('image')}
          />

          <div className="min-w-0">
            <InfoText
              text={product.name}
              className="!text-light-primary dark:!text-dark-primary !text-base truncate"
              data-testid={getTestId('name')}
            />
            {attributesText && (
              <InfoText
                text={attributesText}
                className="!text-light-text-secondary dark:!text-dark-text-secondary !text-sm truncate"
                data-testid={getTestId('attributes')}
              />
            )}
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-6 shrink-0">
          {isExpanded ? (
            <div className="flex flex-col text-start" data-testid={getTestId('qtyCol')}>
              <InfoText
                text={product.quantity.toString()}
                className="!text-light-alert dark:!text-dark-alert !text-sm !font-bold !leading-none"
                data-testid={getTestId('qtyValue')}
              />
              <InfoText
                text={t('logisticsFeedback.quantity')}
                className="!text-light-alert dark:!text-dark-alert !text-xs !font-normal !leading-none mt-1"
                data-testid={getTestId('qtyLabel')}
              />
            </div>
          ) : (
            <>
              <div className="flex flex-col text-start" data-testid={getTestId('qtyCol')}>
                <InfoText
                  text={t('logisticsFeedback.quantity')}
                  className="!text-light-alert dark:!text-dark-alert !text-xs !font-normal !leading-none"
                  data-testid={getTestId('qtyLabel')}
                />
                <InfoText
                  text={product.quantity.toString()}
                  className="!text-light-alert dark:!text-dark-alert !text-sm !font-bold !leading-none mt-1"
                  data-testid={getTestId('qtyValue')}
                />
              </div>

              <div className="flex flex-col text-start" data-testid={getTestId('priceCol')}>
                <InfoText
                  text={t('logisticsFeedback.totalPrice')}
                  className="!text-light-alert dark:!text-dark-alert !text-xs !font-normal !leading-none"
                  data-testid={getTestId('priceLabel')}
                />
                <div
                  className="flex items-center justify-start gap-x-1 mt-1"
                  data-testid={getTestId('priceWrapper')}
                >
                  <InfoText
                    text={product.totalPrice.toLocaleString()}
                    className="!text-light-alert dark:!text-dark-alert !text-sm !font-bold !leading-none"
                    data-testid={getTestId('priceValue')}
                  />
                  <InfoText
                    text={product.currency || t('SAR')}
                    className="!text-light-alert dark:!text-dark-alert !text-sm !font-bold !leading-none"
                    data-testid={getTestId('currency')}
                  />
                </div>
              </div>
            </>
          )}

          {hasCustomization && (
            <button
              type="button"
              onClick={toggle}
              className="shrink-0 text-light-primary dark:text-dark-primary cursor-pointer"
              aria-expanded={isExpanded}
              data-testid={getTestId('toggle')}
            >
              {isExpanded ? <ArrowUp2 size={16} /> : <ArrowDown2 size={16} />}
            </button>
          )}
        </div>
      </div>

      {isExpanded && hasCustomization && (
        <FeedbackProductCustomization
          customization={product.customization}
          imageUrl={product.imageUrl}
          dataTestIdPrefix={getTestId('customization')}
        />
      )}
    </div>
  );
};
